// What the Calls screen holds and reads (office-booking prototype, technical design §6.3), for both of its
// drawings: Riverstone's (Calls.tsx) and Blueprint's (calls/CallsPage.tsx, an open call calls/OpenCall.tsx).
// The phone system's webhook records the call and matches the caller to customers before anyone answers,
// so the screen pops with who it is. Here: today's or the recent calls, the callback queue, the call open
// and its customer, their jobs and glance and what they owe, the notes typed on it (kept while Calls closes,
// QA-771), the booking made on it, the toast, and one live stream for the screen's life (QA-252).
import { useCallback, useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { localDateOf, timeText } from '@flowology/kernel';
import { liveStream } from '../connection/live.ts';
import { client } from '../api.ts';
import type { NavProps } from '../Nav.tsx';
import { useLadder } from '../shared/Ladder.tsx';
import { phoneText, type BookStart, type Call, type Customer, type JobRow } from '../types.ts';
import { callWrite, reviewsCalls } from './reasons-model.ts';
import { useMirror } from '../shared/mirror.ts';
import { LOOK_BACK, type Callback } from './callbacks-model.ts';
import { longDay } from '../records/records-model.ts';
import type { Glance } from './glance-model.ts';
import type { Booked } from '../book/use-book-form.ts';
import { useDesign } from '../blueprint/design.ts';
import { CALL_EVENTS } from './ringing-model.ts';
import { NOTES_SAVE_MS, NoteDrafts, notesValue } from './notes-draft-model.ts';

// a pay link paid, or money recorded, while the customer is on the line (F-40)
const MONEY_EVENTS = /^(payment|invoice)\./;
export const CALL_NOTE = 'Booking from a call keeps the call on the job, so its timeline starts here.';
/** Notes typed and not on their call yet, by call: kept while Calls closes and opens again (QA-771). */
const drafts = new NoteDrafts();
/** A call that rang a moment ago can be asked for before it can be read: asked again once, a moment
 * later, before the screen says it couldn't get it (QA-773). */
const callSoon = async (id: string): Promise<Call> => {
  try {
    return await client.call<Call>('calls.get', { id });
  } catch {
    await new Promise((r) => setTimeout(r, 700));
    return client.call<Call>('calls.get', { id });
  }
};

export const STATUS: Record<string, { chip: string; label: string }> = {
  ringing: { chip: 'sun', label: 'Now, ringing' },
  in_progress: { chip: 'brand', label: 'On the call' },
  completed: { chip: '', label: 'Call ended' },
  missed: { chip: 'warn', label: 'Missed' },
};

export function useCallsScreen({ nav, callId }: { nav: NavProps; callId: string | null }) {
  const zone = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const bp = useDesign() === 'blueprint';
  const clockOf = (iso: string) => timeText(iso, zone);
  const [recent, setRecent] = useState<Call[] | null>(null); // null until it's here: never a false "No calls yet"
  // While the company mirrors ServiceTitan, a call's customer and outcome are ServiceTitan's, and so
  // are new customers, bookings and payments: the reason, notes and who took it stay ours (setReason)
  const mirroring = useMirror();
  const [recentFailed, setRecentFailed] = useState(false);
  const [recentTry, setRecentTry] = useState(0);
  const recentPhase = useLadder(recent === null, recentTry, recentFailed);
  const [call, setCall] = useState<Call | null>(null);
  const [callFailed, setCallFailed] = useState(false);
  const [callTry, setCallTry] = useState(0);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  // the customer at a glance: null with no customer (or none to show); g null while it loads
  const [glance, setGlance] = useState<{ key: string; g: Glance | null } | null>(null);
  const glanceFor = useRef('');
  // any call changing (a reason, an outcome, a review) reads the week's rate again (P-0052)
  const [callTick, setCallTick] = useState(0);
  const [notes, setNotes] = useState('');
  const openRef = useRef(callId);
  openRef.current = callId;
  // the pinned "Not booked…" opens the call's reason picker (BL-099)
  const [pickSignal, setPickSignal] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  // booking on the call (BL-101): the Book screen's own form, in place of the call's cards, for the
  // call it was opened on; then what it booked, said on that call
  const [booking, setBooking] = useState<BookStart | null>(null);
  const [justBooked, setJustBooked] = useState<{ callId: string; b: Booked } | null>(null);
  const bookingRef = useRef<HTMLElement>(null);
  const [moneyTick, setMoneyTick] = useState(0);
  // what they owe, on the glance's stat line (BL-099): for those it shows billed to (who may see
  // invoices), read again when a payment or an invoice changes
  const [owing, setOwing] = useState<{ key: string; cents: number } | null>(null);
  // Calls, or the booking rate (BL-003): a CSR's own, everyone's for an owner or manager. The callback
  // queue is no tab of its own: it is always in the queue beside the call (BL-099)
  const [part, setPart] = useState<'calls' | 'rate'>('calls');
  // the callback queue (item 2): read with the screen and again on any call change, for its count
  const [backs, setBacks] = useState<{ calls: Callback[] | null; days: number; failed: boolean }>({
    calls: null,
    days: LOOK_BACK[0],
    failed: false,
  });
  const [backsTry, setBacksTry] = useState(0);
  const held = nav.permissions ?? [];
  const everyone = reviewsCalls(held);
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  /** "2:14 p.m." today, "Oct 15, 2026, 2:14 p.m." before. */
  const whenOf = (iso: string) =>
    localDateOf(new Date(iso), zone) === today
      ? clockOf(iso)
      : `${longDay(localDateOf(new Date(iso), zone))}, ${clockOf(iso)}`;

  // Blueprint's Inbox › Calls is today's: its calls, and how many there were and booked (the day's line)
  const [day, setDay] = useState<{ total: number; booked: number } | null>(null);
  const shown = useRef(false); // Recent calls is on screen: a failed re-read only toasts
  const loadRecent = useCallback(async () => {
    try {
      const r = await client.call<{ calls: Call[]; total: number; booked: number }>(
        'calls.recent',
        bp ? { from: today, to: today, limit: 100 } : { limit: 15 },
      );
      setRecent(r.calls);
      if (bp) setDay({ total: r.total, booked: r.booked });
      setRecentFailed(false);
      shown.current = true;
    } catch (e) {
      if (shown.current) setToast(e instanceof RpcError ? e.message : 'Could not load calls.');
      else setRecentFailed(true);
    }
  }, [bp, today]);

  const loadCall = useCallback(async () => {
    if (!callId) {
      setCall(null);
      setCustomer(null);
      glanceFor.current = '';
      setGlance(null);
      return;
    }
    // The call, its customer and their jobs arrive together, so the screen pop fills in at once (L-sheet).
    const c = await callSoon(callId);
    const open = c.customerId ?? c.openCustomerId;
    const [cust, js] = open
      ? await Promise.all([
          client.call<Customer>('customers.get', { id: open }),
          client.call<{ jobs: JobRow[] }>('jobs.list', { customerId: open, limit: 5 }),
        ])
      : [null, { jobs: [] }];
    // another call opened meanwhile: this answer is no longer the screen's
    if (openRef.current !== callId) return;
    setCall(c);
    // what's typed and not on the call yet stays in the box: a re-read never empties it (QA-771)
    setNotes(drafts.shown(c.id, c.notes));
    setCustomer(cust);
    setJobs(js.jobs);
    // the glance fills in after: the card never waits on it. By the customer customers.get gave (a
    // merged one's kept customer), and only if it's still the one open when it answers.
    const key = cust ? `${cust.id}:${c.locationId ?? ''}` : '';
    glanceFor.current = key;
    if (!cust) setGlance(null);
    else {
      setGlance((was) => (was && was.key === key ? was : { key, g: null }));
      void client
        .call<Glance>('customers.glance', {
          customerId: cust.id,
          ...(c.locationId ? { locationId: c.locationId } : {}),
        })
        .then((g) => glanceFor.current === key && setGlance({ key, g }))
        .catch(() => glanceFor.current === key && setGlance(null));
    }
    setCallFailed(false);
  }, [callId]);

  const owingFor = glance?.g && glance.g.billedCents !== null ? glance.key : '';
  const owingCustomer = glance?.g?.customerId;
  useEffect(() => {
    if (!owingFor || !owingCustomer) return;
    let live = true;
    void client
      .call<{ openCents: number }>('customers.money', { customerId: owingCustomer })
      .then((m) => live && setOwing({ key: owingFor, cents: m.openCents }))
      .catch(() => live && setOwing(null));
    return () => {
      live = false;
    };
  }, [owingFor, owingCustomer, moneyTick]);

  // the form opens at its top, wherever the pinned button was pressed
  useEffect(() => {
    if (booking) bookingRef.current?.scrollIntoView({ block: 'nearest' });
  }, [booking]);

  // A popped call (callId changes) is also re-read into Recent, whichever way it arrived.
  useEffect(() => void loadRecent(), [loadRecent, callId, recentTry]);
  // the queue: with the screen, on any call change, and after an action on it
  useEffect(() => {
    let live = true;
    void client
      .call<{ calls: Callback[] }>('calls.callbacks', { days: backs.days })
      .then((r) => live && setBacks((b) => ({ ...b, calls: r.calls, failed: false })))
      .catch(() => live && setBacks((b) => ({ ...b, failed: true })));
    return () => {
      live = false;
    };
  }, [backs.days, callTick, backsTry]);
  useEffect(() => {
    // a new call starts as loading, never as the last one's failure (QA-773)
    setCallFailed(false);
    void loadCall().catch(() => openRef.current === callId && setCallFailed(true));
  }, [loadCall, callTry, callId]);
  // A call that is asked for but not here yet (or not this one) is loading.
  const callPending = !!callId && call?.id !== callId;
  const callPhase = useLadder(callPending, callTry, callFailed);

  // One stream for the screen's life (QA-252): reopening it on every new call lost the event that
  // caused the change. The handler reads the latest loaders through a ref.
  const loaders = useRef({ loadRecent, loadCall });
  loaders.current = { loadRecent, loadCall };
  useEffect(() => {
    const es = liveStream();
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse((e as MessageEvent<string>).data) as { type: string };
      if (MONEY_EVENTS.test(type)) return setMoneyTick((n) => n + 1);
      if (!CALL_EVENTS.test(type)) return;
      setCallTick((n) => n + 1);
      void loaders.current.loadRecent();
      void loaders.current.loadCall().catch(() => undefined);
    });
    // After a reconnect, catch up on whatever rang meanwhile.
    es.addEventListener('open', () => void loaders.current.loadRecent());
    return () => es.close();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const update = async (patch: Record<string, unknown>, done?: string) => {
    if (!call) return;
    try {
      setCall(await client.call<Call>(callWrite(mirroring), { id: call.id, ...patch }));
      if (done) setToast(done);
    } catch (e) {
      setToast(e instanceof RpcError ? e.message : 'Could not save.');
    }
  };
  // the notes on the call as typed: a pause saves them, so does leaving the box or the call (QA-771)
  const notesTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveNotes = async (id: string) => {
    clearTimeout(notesTimer.current);
    const text = drafts.pending(id);
    if (text === null) return;
    try {
      const r = await client.call<Call>(callWrite(mirroring), { id, notes: notesValue(text) });
      drafts.saved(id, r.notes);
      if (openRef.current === id) setCall(r);
    } catch (e) {
      setToast(`Notes not saved yet: ${e instanceof RpcError ? e.message : 'try again.'}`);
    }
  };
  const saveNotesRef = useRef(saveNotes);
  saveNotesRef.current = saveNotes;
  useEffect(() => {
    if (!callId) return;
    return () => void saveNotesRef.current(callId);
  }, [callId]);
  const typeNotes = (id: string, text: string) => {
    setNotes(text);
    drafts.type(id, text);
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(() => void saveNotesRef.current(id), NOTES_SAVE_MS);
  };
  // recording a call is an edit: someone who may only see calls isn't offered it (QA-773)
  const records = held.includes('customers.edit');
  // the week's rate is read again when a call's reason, outcome or review may have changed (P-0052)
  const rateVersion = `${callTick}:${call?.id ?? ''}:${call?.outcome ?? ''}:${call?.reason?.id ?? ''}:${call?.reviewed?.at ?? ''}`;

  const status = call ? (STATUS[call.status] ?? { chip: '', label: call.status }) : null;
  // Blueprint: the call's page settles in as the call open on it is answered (DESIGN.md §9.4)
  const seen = useRef<{ id: string; status: string } | null>(null);
  const [settle, setSettle] = useState<string | null>(null);
  useEffect(() => {
    if (!call) return;
    const was = seen.current;
    if (was && was.id === call.id && was.status === 'ringing' && call.status === 'in_progress')
      setSettle(call.id);
    seen.current = { id: call.id, status: call.status };
  }, [call]);
  const bookingHere = booking && call && booking.callId === call.id ? booking : null;
  const bookedHere = justBooked && call && justBooked.callId === call.id ? justBooked.b : null;
  // the job the call booked, by its number: the customer's recent jobs, else what was just booked
  const callJob = call?.jobId
    ? (jobs.find((j) => j.id === call.jobId)?.number ??
      (bookedHere?.jobId === call.jobId ? bookedHere.jobNumber : null))
    : null;
  const startBooking = (start: BookStart) => {
    setJustBooked(null);
    setBooking(start);
  };
  const who = customer
    ? `${customer.contacts[0]?.name ?? customer.displayName} · ${customer.displayName}`
    : call && call.matches.length > 1
      ? `${call.matches.length} customers have ${phoneText(call.from)}`
      : call
        ? `Unknown caller ${phoneText(call.from)}`
        : '';

  return {
    zone,
    bp,
    clockOf,
    whenOf,
    today,
    mirroring,
    held,
    everyone,
    records,
    recent,
    recentPhase,
    recentFailed,
    retryRecent: () => setRecentTry((n) => n + 1),
    day,
    call,
    setCall,
    callPending,
    callPhase,
    retryCall: () => setCallTry((n) => n + 1),
    loadCall,
    customer,
    jobs,
    glance,
    owing,
    moneyTick,
    notes,
    typeNotes,
    saveNotes,
    pickSignal,
    pickReason: () => setPickSignal((n) => n + 1),
    toast,
    setToast,
    booking: bookingHere,
    bookingRef,
    startBooking,
    stopBooking: () => setBooking(null),
    booked: bookedHere,
    /** The form on the call booked: what it booked is said on that call. */
    onBooked: (b: Booked) => {
      if (!call) return;
      setBooking(null);
      setJustBooked({ callId: call.id, b });
      void loadCall();
    },
    part,
    setPart,
    backs,
    olderBacks: (days: number) => setBacks((b) => ({ ...b, days })),
    retryBacks: () => setBacksTry((n) => n + 1),
    update,
    rateVersion,
    status,
    settle: !!call && settle === call.id,
    callJob,
    who,
  };
}

export type CallsScreenState = ReturnType<typeof useCallsScreen>;
